import whiteHorizontalLogo from "../../pages/images/logo-white-horizontal.png";
import { Link } from "react-router-dom";
import localeText from "../../locale";

import "./Navbar.css";
import { useState } from "react";
import { useEffect } from "react";
import GetUserInfoService from "../../service/GetUserInfoService";
import { useRef } from "react";
import { Toast } from "primereact/toast";
import Wrapper from "../Wrapper/Wrapper";
import { useFavoritesContext } from "../../contexts/FavoritesContext/FavoritesContext";
import { useUserContext } from "../../contexts/UserContext/UserContext";
import SearchInput from "../SearchProducts/Components/SearchInput/SearchInput";
import NavLinks from "../NavLinks/NavLinks";
import NavDepartmentSearch from "../NavDepartmentSearch/NavDepartmentSearch";
import { useLocation } from "react-router-dom";
import StoreNavDropdown from "../StoreNavDropdown/StoreNavDropdown";
import LoginDialog from "../LoginDialog/LoginDialog";
import RegisterDialog from "../RegisterDialog/RegisterDialog";
import OrderService from "../../service/OrderService";
import { useAuthDialog } from "../../contexts/AuthDialogContext/AuthDialogContext";

const Navbar = (props) => {
  const location = useLocation();

  const isFringeBenefitsPage = location?.pathname?.toLowerCase()?.includes("fringe");

  const orderKey = isFringeBenefitsPage
    ? "__extraconomy_order_fringe_benefit"
    : "__extraconomy_order";

  const { favoritesCount } = useFavoritesContext();

  const [loadingUserInfo, setLoadingUserInfo] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const { userInfo, setUserInfo } = useUserContext();

  const [numberOfCartItems, setNumberOfCartItems] = useState(-1);

  const {
    showLoginDialog,
    showRegisterDialog,
    loginFromCheckout,
    openLoginDialog,
    openRegisterDialog,
    closeLoginDialog,
    closeRegisterDialog,
    switchToRegister,
    switchToLogin,
  } = useAuthDialog();

  const userInfoService = new GetUserInfoService();
  const orderService = new OrderService();

  const toast = useRef(null);

  const [expression, setExpression] = useState("");
  const [category, setCategory] = useState(null);

  const isCartPage = location?.pathname === "/cart" || location?.pathname === "/cart-fringe-benefit";

  useEffect(() => {
    let cancel = false;

    async function fetchUserInfo() {
      const token = localStorage.getItem("__extraconomy_token");
      const username = localStorage.getItem("__extraconomy_username");

      if (token) {
        try {
          setLoadingUserInfo(true);

          const resType = await userInfoService.getUserTypeInfo(username);
          if (cancel) return;

          if (resType.result === "Admin") {
            setIsAdmin(true);
            setLoadingUserInfo(false);
            return;
          }

          const res = await userInfoService.getUserInfo(token, username);

          if (res.messageCode !== 200001) {
            toast.current.show({
              severity: "error",
              summary: localeText("error"),
              detail: localeText("data").find((msg) => msg.code === res.messageCode).message,
              life: 6000,
            });
            setLoadingUserInfo(false);
          } else {
            setUserInfo(res.result.user);
            setLoadingUserInfo(false);
          }
        } catch (e) {
          console.log(e);
        }
      }
    }

    fetchUserInfo();

    return () => {
      cancel = true;
    };
  }, []);

  // Show number of items in cart
  useEffect(() => {
    const updateCartItems = () => {
      if (localStorage.getItem(orderKey)) {
        let order = JSON.parse(localStorage.getItem(orderKey));

        if (isFringeBenefitsPage) order = { itens: [order] };

        setNumberOfCartItems(order?.itens?.length ?? 0);
      }
    };

    updateCartItems();

    window.addEventListener('storage', updateCartItems);

    return () => {
      window.removeEventListener('storage', updateCartItems);
    };
  }, [orderKey, isFringeBenefitsPage]);

  const handleLoginSuccess = async (fromCheckout) => {
    closeLoginDialog();

    if (fromCheckout) {
      try {
        await mergeGuestCart();
      } catch (error) {
        console.error("Error merging guest cart:", error);
      }
    }

    setTimeout(() => {
      window.location.reload();
    }, 100);
  };

  const handleRegisterSuccess = () => {
    closeRegisterDialog();
    openLoginDialog(loginFromCheckout);
  };

  async function mergeGuestCart() {
    try {
      const regularOrderKey = "__extraconomy_order";
      const guestOrder = localStorage.getItem(regularOrderKey);

      if (guestOrder) {
        const parsedGuestOrder = JSON.parse(guestOrder);

        if (parsedGuestOrder.isGuestCart && parsedGuestOrder.itens?.length > 0) {
          await createOrderFromGuestCart(parsedGuestOrder);
        }
      }
    } catch (e) {
      console.log("Error merging guest cart:", e);
    }
  }

  async function createOrderFromGuestCart(guestOrder) {
    try {
      const orderKey = "__extraconomy_order";
      const firstItem = guestOrder.itens[0];

      const res = await orderService.postCreateOrder(
        localStorage.getItem("__extraconomy_token"),
        localStorage.getItem("__extraconomy_username"),
        firstItem.product.user.username,
        firstItem.product.id,
        firstItem.quantity,
        0,
        firstItem.productVariation?.id || null,
        firstItem.shareToken
      );

      if (res.messageCode === 200000) {
        let order = res.result;

        for (let i = 1; i < guestOrder.itens.length; i++) {
          const item = guestOrder.itens[i];
          const addRes = await orderService.postAddItem(
            localStorage.getItem("__extraconomy_token"),
            localStorage.getItem("__extraconomy_username"),
            order.id,
            item.product.id,
            item.quantity,
            0,
            item.productVariation?.id || null,
            item.shareToken
          );

          if (addRes.messageCode === 200000) {
            order = addRes.result;
          }
        }

        localStorage.setItem(orderKey, JSON.stringify(order));
      } else {
        localStorage.removeItem(orderKey);
      }
    } catch (e) {
      console.error("Error creating order from guest cart:", e);
      localStorage.removeItem("__extraconomy_order");
    }
  }

  const shouldChangeColor = isCartPage && !userInfo;
  const navbarBgColor = shouldChangeColor ? "#2E4E5C" : undefined;
  const navbarClasses = shouldChangeColor
    ? "grid grid-nogutter align-items-center w-full sm:px-4 sm:py-3 px-3 py-3"
    : "grid grid-nogutter align-items-center w-full bg-primary-green sm:px-4 sm:py-3 px-3 py-3";

  return (
    <div className="surface-0 sticky top-0 z-5">
      <LoginDialog
        visible={showLoginDialog}
        onHide={closeLoginDialog}
        onLoginSuccess={handleLoginSuccess}
        onOpenRegister={switchToRegister}
        fromCheckout={loginFromCheckout}
      />

      <RegisterDialog
        visible={showRegisterDialog}
        onHide={closeRegisterDialog}
        onRegisterSuccess={handleRegisterSuccess}
        onOpenLogin={switchToLogin}
      />

      <Wrapper
        classnames={navbarClasses}
        style={navbarBgColor ? { backgroundColor: navbarBgColor } : {}}
      >
        <Toast ref={toast} />
        <div className="md:hidden flex h-full">
          <StoreNavDropdown categories={props.categories} onSearch={props.onSearch} />
        </div>
        <Link to="/shop">
          <img
            src={whiteHorizontalLogo}
            alt="Extraconomy logo white"
            className="logo-responsive"
          ></img>
        </Link>
        {props.hasSearch && (
          <SearchInput
            expression={expression}
            category={category}
            isLoading={props.isLoading}
            onSearch={props.onSearch}
            setExpression={setExpression}
            setCategory={setCategory}
            categories={props.categories}
            handleSelectedCategory={props.handleSelectedCategory}
            selectedCategory={props.selectedCategory}
            loadingCategories={props.loadingCategories}
          />
        )}

        <NavLinks
          isAdmin={isAdmin}
          userInfo={userInfo}
          loadingUserInfo={loadingUserInfo}
          favoritesCount={favoritesCount}
          numberOfCartItems={numberOfCartItems}
          hasSearch={props.hasSearch}
          onOpenLoginDialog={() => openLoginDialog(false)}
          onOpenRegisterDialog={openRegisterDialog}
        />
      </Wrapper>
      {props.hasSearch && (
        <NavDepartmentSearch categories={props.categories} onSearch={props.onSearch} />
      )}
    </div>
  );
};

export default Navbar;
